import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'

const Manage_LiveCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const navigate = useNavigate();

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCategories = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_Baseurl}/admin/livestream-categories`, {
        headers: getAuthHeaders(),
        params: { page }
      });
      
      if (response.data.status) {
        setCategories(response.data.AllLiveStreamCategories || response.data.data || []);
        setCurrentPage(response.data.pagination?.currentPage || page);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_Baseurl}/admin/livestream-categories/${id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.status) {
        alert('Category deleted successfully!');
        fetchCategories(currentPage); // Refresh current page
      } else {
        alert('Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category');
    }
    setDeleteConfirm({ show: false, id: null });
  };

  const handlePrevClick = () => {
    if (currentPage > 1) {
      fetchCategories(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      fetchCategories(currentPage + 1);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="pb-5">
      <div className="bootstrapTable theme-bg-color p-3 rounded-2">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-0">
              Live Stream Categories ({categories.length} results found)
            </h4>
          </div>
          <div>
            <Link to="/admin/content/add-manage-live-category" className="btn btn-primary">
              <i className="fa fa-plus-circle me-2"></i>Add Categories
            </Link>
          </div>
        </div>

        {/* Table Header */}
        <div className="row m-0 theme-border-bottom-secondary pb-3">
          <div className="col-lg-4 col-md-4 col-sm-4 col-4">
            <label className="theme-text-color fw-bold fs-6">Category</label>
          </div>
          <div className="col-lg-3 col-md-3 col-sm-3 col-3">
            <label className="theme-text-color fw-bold fs-6">Uploaded by</label>
          </div>
          <div className="col-lg-3 col-md-3 col-sm-3 col-3">
            <label className="theme-text-color fw-bold fs-6">Actions</label>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading categories...</p>
          </div>
        ) : (
          <div className="pb-5">
            {categories.length > 0 ? (
              categories.map((item, key) => (
                <div key={key} className="theme-bg-color theme-border-bottom-secondary px-3 py-3">
                  <div className="row m-0 p-0 align-items-center">
                    <div className="col-lg-4 col-md-4 col-sm-4 col-4">
                      <div className="d-flex align-items-center">
                        <img 
                          src={item.image || item.image_url || '/default-category.png'} 
                          className="profile me-3" 
                          alt="Category" 
                          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                          onError={(e) => {
                            e.target.src = '/default-category.png';
                          }}
                        />
                        <div>
                          <div className="table-heading theme-text-color">
                            {item.name.length > 15 ? `${item.name.slice(0, 15)}...` : item.name}
                          </div>
                          <div className="table-subheading text-muted">
                            {item.upload_on}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-3 col-sm-3 col-3">
                      <span className="theme-text-color">
                        {item.uploaded_by_name || 'Admin'}
                      </span>
                    </div>
                    <div className="col-lg-3 col-md-3 col-sm-3 col-3">
                      <div className="editdropdown">
                        <span className="editdropdown-button">
                          <i className="fa fa-ellipsis-v theme-text-color"></i>
                        </span>
                        <div className="editdropdown-menu theme-bg-color">
                          <Link 
                            to={`/admin/livestream-edit-categories/${item.id}`}
                            className="theme-text-color theme-bg-color d-flex align-items-center p-2"
                          >
                            <i className="fa fa-pencil-square me-2"></i>
                            Edit
                          </Link>
                          <div 
                            onClick={() => setDeleteConfirm({ show: true, id: item.id })}
                            className="d-flex align-items-center p-2 theme-bg-color theme-text-color"
                            style={{ cursor: 'pointer' }}
                          >
                            <i className="fa fa-trash me-2"></i>
                            Delete
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="mb-0">No categories found</p>
                <small className="text-muted">Click 'Add Categories' to create your first category</small>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {categories.length > 0 && (
          <div className="row text-center d-flex mt-3">
            <div className="col-lg-4">
              <button
                type="button"
                className={`btn btn-primary ${currentPage <= 1 ? 'disabled' : ''}`}
                onClick={handlePrevClick}
                disabled={currentPage <= 1}
              >
                {"<< Previous"}
              </button>
            </div>
            <div className="col-lg-4">
              <p className="mb-0">
                Page {currentPage} of {totalPages}
              </p>
            </div>
            <div className="col-lg-4">
              <button
                type="button"
                className={`btn btn-primary ${currentPage >= totalPages ? 'disabled' : ''}`}
                onClick={handleNextClick}
                disabled={currentPage >= totalPages}
              >
                {"Next >>"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setDeleteConfirm({ show: false, id: null })}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this category? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setDeleteConfirm({ show: false, id: null })}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => handleDelete(deleteConfirm.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Manage_LiveCategory